"use client";
import React from "react";

import { Box, Icon } from "../../foundations";
import { Text } from "../../foundations/Typography/Text";
import { useId } from "../../hooks/useId";
import { useWarning } from "../../hooks/useWarning";
import { Checkbox } from "../Checkbox";

export interface TableProps {
  children: React.ReactElement | React.ReactElement[];
  variant?: "shaded" | "unshaded";
  spacing?: "default" | "compact";
}

interface TableCellProps {
  children?: React.ReactNode | React.ReactNode[];
  grow?: number;
}

interface TableCellWrapperProps {
  sortable?: boolean;
  sortDirection?: "asc" | "desc";
  children: React.ReactNode | React.ReactNode[];
  visibleOnHoverClassName?: string;
}

interface TableRowProps {
  children:
    | React.ReactElement<TableCellProps>
    | React.ReactElement<TableCellProps>[];
  selected?: boolean;
}

interface TableHeaderSelectionCellProps {
  checked: boolean;
  indeterminate?: boolean;
  onClick: () => void;
  disabled?: boolean;
}

interface TableSelectionCellProps {
  checked: boolean;
  onClick: () => void;
  disabled?: boolean;
}

export interface TableContextProps {
  variant: "shaded" | "unshaded";
  spacing: "default" | "compact";
  totalGrow?: number;
}

export const TableContext = React.createContext<TableContextProps>({
  variant: "shaded",
  spacing: "default"
});

function getGrowValuesFromRow(row: React.ReactElement<TableRowProps>) {
  const growValues: number[] = [];
  React.Children.forEach(
    row.props.children,
    (cell: React.ReactElement<TableCellProps>) => {
      if (
        cell.type === TableHeaderSelectionCell ||
        cell.type === TableSelectionCell
      ) {
        return;
      }
      const g = cell.props?.grow ?? 1;
      growValues.push(g);
    }
  );
  return growValues;
}

const getScrollableAncestor = (
  node: HTMLElement | null
): HTMLElement | Window => {
  while (node) {
    const overflowY = getComputedStyle(node).overflowY;
    if (
      node.scrollHeight > node.clientHeight &&
      (overflowY === "auto" || overflowY === "scroll")
    ) {
      return node;
    }
    node = node.parentElement;
  }
  return window;
};

const useStickyHeaderDetection = <T extends HTMLElement>() => {
  const ref = React.useRef<T | null>(null);
  const [isSticky, setIsSticky] = React.useState(false);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }

    const scrollContainer = getScrollableAncestor(el) as HTMLElement | Window;

    const handleScroll = () => {
      const rect = el.getBoundingClientRect();
      setIsSticky(Math.floor(rect.top) <= 0);
    };

    handleScroll();

    scrollContainer.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return { ref, isSticky };
};

const Table: React.FC<TableProps> = (props) => {
  const { variant = "shaded", spacing = "default", children } = props;
  const { warn } = useWarning();

  let columnCount = 0;
  let hasHeaderRow = false;
  let columnGrowValues: number[] = [];
  let totalGrow = 0;
  let foundFirstRow = false;

  React.Children.forEach(children, (child) => {
    if (child.type === TableHeaderRow) {
      hasHeaderRow = true;
      React.Children.forEach(child.props.children, (row) => {
        if (!foundFirstRow && row.type === TableRow) {
          foundFirstRow = true;
          columnGrowValues = getGrowValuesFromRow(row);
        }
      });
    } else if (child.type === TableBody) {
      React.Children.forEach(child.props.children, (row) => {
        if (row.type === TableRow) {
          if (columnCount === 0) {
            columnCount = React.Children.count(row.props.children);
          }
          if (!foundFirstRow) {
            foundFirstRow = true;
            columnGrowValues = getGrowValuesFromRow(row);
          }
        }
      });
    }
  });

  if (!hasHeaderRow) {
    warn(
      "TableV2 is missing a TableHeaderRow. It's recommended to include a header row for better accessibility and structure."
    );
  }

  totalGrow = columnGrowValues.reduce((acc, val) => acc + val, 0);
  if (totalGrow === 0) {
    totalGrow = columnGrowValues.length || 1;
  }
  if (columnCount === 0) {
    columnCount = columnGrowValues.length || 1;
  }

  const context = {
    variant,
    spacing,
    totalGrow
  };

  return (
    <TableContext.Provider value={context}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "spacing-2",
          borderRadius: "radius-2",
          backgroundColor: "surface-neutral-default"
        }}
      >
        <Box
          as="table"
          sx={{
            borderCollapse: "separate",
            tableLayout: "fixed",
            minWidth: "100%"
          }}
        >
          {children}
        </Box>
      </Box>
    </TableContext.Provider>
  );
};

const TableHeaderRow: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { variant } = React.useContext(TableContext);
  const { warn } = useWarning();
  const { ref, isSticky } = useStickyHeaderDetection<HTMLTableSectionElement>();

  React.Children.forEach(children, (child) => {
    if (React.isValidElement(child) && child.type !== TableRow) {
      warn("TableHeaderRow should only contain TableRow as a direct child.");
    }
  });

  return (
    <Box
      as="thead"
      ref={ref}
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 1,
        width: "100%",
        backgroundColor:
          variant === "shaded"
            ? "surface-neutral-secondary"
            : "surface-neutral-default",
        borderTopLeftRadius: () => (isSticky ? "0" : "radius-2"),
        borderTopRightRadius: () => (isSticky ? "0" : "radius-2"),
        outlineColor: "stroke-neutral-emphasis",
        outlineOffset: "-1px",
        outlineStyle: "solid",
        outlineWidth: "1px",
        // @ts-ignore
        "tr:first-of-type th:first-of-type": {
          borderTopLeftRadius: () => (isSticky ? "0" : "radius-2")
        },
        "tr:first-of-type th:last-child": {
          borderTopRightRadius: () => (isSticky ? "0" : "radius-2")
        }
      }}
    >
      {children}
    </Box>
  );
};

export interface TableHeaderCellProps extends TableCellProps {
  sortable?: boolean;
  sortKey?: string;
  onClick?: (columnKey?: string) => void;
  sortDirection?: "asc" | "desc";
}

const TableHeaderCell: React.FC<TableHeaderCellProps> = ({
  grow,
  children,
  sortable = false,
  sortKey,
  onClick,
  sortDirection
}) => {
  const { variant, totalGrow = 1 } = React.useContext(TableContext);
  const visibleOnHoverClassName = `tableHeaderIcon${useId().replace(/^[A-Z0-9]gi/, "")}`;

  if (grow !== undefined && grow < 0) {
    throw new Error("grow property must be >= 0.");
  }
  const fraction = (grow ?? 1) / totalGrow;

  const isEmpty =
    children === null ||
    children === undefined ||
    (Array.isArray(children) && children.length === 0);

  const handleClick = () => {
    if (sortable && onClick) {
      onClick(sortKey);
    }
  };

  const isSorted = !!sortDirection;
  const ariaSort = isSorted
    ? sortDirection === "desc"
      ? "descending"
      : "ascending"
    : undefined;

  const textColor = React.useMemo(() => {
    if (variant === "shaded") {
      return "text-default";
    }
    return "text-emphasis";
  }, [variant]);

  return (
    <Box
      as="th"
      onClick={handleClick}
      role={sortable ? "button" : undefined}
      tabIndex={sortable ? 0 : undefined}
      aria-sort={ariaSort}
      aria-hidden={isEmpty ? "true" : undefined}
      sx={{
        cursor: sortable ? "pointer" : "default",
        width: () => `${fraction * 100}%`,
        borderBottom: "1px solid",
        borderBottomColor:
          variant === "shaded"
            ? "stroke-neutral-emphasis"
            : "stroke-brand-emphasis",
        color: textColor,
        transition: "transition-color-medium",
        // @ts-ignore
        "&:not(:first-of-type)": variant === "shaded" && {
          borderLeft: "1px solid",
          borderLeftColor: "stroke-neutral-tertiary"
        },
        [`.${visibleOnHoverClassName}`]: {
          opacity: 0,
          transition: "transition-color-medium"
        },
        ...(sortable && {
          ":hover": {
            color: "text-emphasis",
            backgroundColor:
              variant === "shaded"
                ? "surface-neutral-tertiary"
                : "surface-neutral-secondary"
          },
          [`:hover .${visibleOnHoverClassName}`]: {
            opacity: 1
          },
          ":active": {
            color: "text-emphasis",
            backgroundColor:
              variant === "shaded"
                ? "color-system-gray-200"
                : "surface-neutral-tertiary"
          },
          [`:active .${visibleOnHoverClassName}`]: {
            opacity: 1
          }
        })
      }}
    >
      <TableCellWrapper
        sortable={sortable}
        sortDirection={sortDirection}
        visibleOnHoverClassName={visibleOnHoverClassName}
      >
        {children}
      </TableCellWrapper>
    </Box>
  );
};

const TableBody: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { variant } = React.useContext(TableContext);
  const { warn } = useWarning();

  React.Children.forEach(children, (child) => {
    if (React.isValidElement(child) && child.type !== TableRow) {
      warn("TableBody should only contain TableRow as a direct child.");
    }
  });

  return (
    <Box
      as="tbody"
      sx={{
        borderBottomLeftRadius: "radius-2",
        borderBottomRightRadius: "radius-2",
        outlineColor: "stroke-neutral-emphasis",
        outlineOffset: "-1px",
        outlineStyle: "solid",
        outlineWidth: "1px",
        position: "relative",
        top: () => "-1px",
        // @ts-ignore
        "tr:not([data-selected='true']):nth-of-type(even)": {
          backgroundColor:
            variant === "shaded"
              ? "surface-neutral-secondary"
              : "surface-neutral-default"
        },
        "tr:not([data-selected='true']):nth-of-type(odd)": {
          backgroundColor: "surface-neutral-default"
        },
        "tr[data-selected='true']": {
          backgroundColor: "surface-brand"
        },
        "&:not(:last-child) tr": {
          borderBottom: "1px solid",
          borderColor: "stroke-neutral-secondary"
        },
        "tr:first-of-type td:first-of-type": {
          borderTopLeftRadius: "radius-2"
        },
        "tr:first-of-type td:last-child": {
          borderTopRightRadius: "radius-2"
        },
        "tr:last-of-type td:first-of-type": {
          borderBottomLeftRadius: "radius-2"
        },
        "tr:last-of-type td:last-child": {
          borderBottomRightRadius: "radius-2"
        }
      }}
    >
      {children}
    </Box>
  );
};

const TableRow: React.FC<TableRowProps> = ({ children, selected }) => {
  const { warn } = useWarning();

  React.Children.forEach(children, (child) => {
    if (
      React.isValidElement(child) &&
      child.type !== TableHeaderCell &&
      child.type !== TableCell
    ) {
      warn(
        "TableRow should only contain TableHeaderCell (if in TableHeaderRow) or TableCell (if in TableBody)."
      );
    }
  });

  return (
    <Box
      as="tr"
      data-selected={selected ? "true" : undefined}
      sx={{
        width: "100%",
        // @ts-ignore
        "&:not(:last-of-type) td": {
          borderBottom: "1px solid",
          borderColor: "stroke-neutral-secondary"
        }
      }}
    >
      {children}
    </Box>
  );
};

const TableCellWrapper: React.FC<TableCellWrapperProps> = ({
  visibleOnHoverClassName,
  sortable = false,
  sortDirection,
  children
}) => {
  const { spacing } = React.useContext(TableContext);

  return (
    <Box
      as="span"
      sx={{
        alignItems: "start",
        color: "currentColor",
        display: "flex",
        gap: "spacing-3",
        justifyContent: sortable ? "space-between" : "flex-start",
        px: spacing === "default" ? "spacing-4" : "spacing-3",
        py: spacing === "default" ? "spacing-3" : "spacing-2",
        textAlign: "left"
      }}
    >
      <Text
        variant="body-2-medium"
        sx={{
          color: "currentColor",
          overflowWrap: "normal",
          whiteSpace: "nowrap",
          wordBreak: "keep-all"
        }}
      >
        {children}
      </Text>
      {sortable && (
        <Box
          as="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            flexShrink: 0,
            height: 5
          }}
        >
          {sortDirection === "asc" ? (
            <Icon icon="arrow-up" size="xxs" color="onSurface-brand-primary" />
          ) : sortDirection === "desc" ? (
            <Icon
              icon="arrow-down"
              size="xxs"
              color="onSurface-brand-primary"
            />
          ) : (
            <Icon
              className={visibleOnHoverClassName}
              icon="arrows-down-up"
              size="xxs"
              color="onSurface-brand-primary"
            />
          )}
        </Box>
      )}
    </Box>
  );
};

const TableCell: React.FC<TableCellProps> = ({ grow, children }) => {
  const { warn } = useWarning();
  const { variant } = React.useContext(TableContext);
  if (grow) {
    warn(
      "Grow on Table Cell will be deprecated in a future release and does not currently impact the calculation of grow on the table"
    );
  }

  return (
    <Box
      as="td"
      sx={{
        // @ts-ignore
        "&:not(:first-of-type)": variant === "shaded" && {
          borderLeft: "1px solid",
          borderColor: "stroke-neutral-secondary"
        }
      }}
    >
      <TableCellWrapper>{children}</TableCellWrapper>
    </Box>
  );
};

const TableHeaderSelectionCell: React.FC<TableHeaderSelectionCellProps> = ({
  checked,
  indeterminate = false,
  onClick,
  disabled = false
}) => {
  const { spacing } = React.useContext(TableContext);
  return (
    <Box
      as="th"
      sx={{
        verticalAlign: "middle",
        justifyItems: "center",
        px: spacing === "default" ? "spacing-4" : "spacing-3"
      }}
    >
      <Checkbox
        label=""
        value={checked}
        indeterminate={indeterminate}
        onChange={onClick}
        disabled={disabled}
        ariaLabel="Select all rows"
      />
    </Box>
  );
};

const TableSelectionCell: React.FC<TableSelectionCellProps> = ({
  checked,
  onClick,
  disabled = false
}) => {
  const { spacing } = React.useContext(TableContext);
  return (
    <Box
      as="td"
      sx={{
        verticalAlign: "middle",
        justifyItems: "center",
        px: spacing === "default" ? "spacing-4" : "spacing-3"
      }}
    >
      <Checkbox
        label=""
        value={checked}
        onChange={onClick}
        disabled={disabled}
        ariaLabel="Select row"
      />
    </Box>
  );
};

export {
  Table,
  TableBody,
  TableCell,
  TableHeaderCell,
  TableHeaderRow,
  TableHeaderSelectionCell,
  TableRow,
  TableSelectionCell
};
